import React, { useEffect, useContext, createContext } from 'react';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { setLoader } from '../../redux/slices/loader';
import { setUserDetails } from '../../redux/slices/userDetails';
import { logout } from '../../redux/store';
import notification from '../notification';
import {
  getUser,
  setUserAuthToken,
  setUserRefreshToken
} from '../../utils/api-helper';
import { wsDisconnect } from '../../utils/wsManager';
import ROUTES from '../../constants/ROUTES';

export const AuthContext = createContext();

const Auth = (props) => {
  const { children } = props;
  const dispatch = useDispatch();
  const navigation = useNavigate();
  const { userDetails } = useSelector((state) => state.userDetails);

  const pathname = window.location?.pathname;

  const loadUserDetails = async (userToken) => {
    try {
      const sessionUserToken =
        userToken ||
        JSON.parse(window.sessionStorage.getItem('authorization'))?.token;

      setUserAuthToken(sessionUserToken);
      setUserRefreshToken(
        JSON.parse(window.sessionStorage.getItem('authorization'))?.refreshToken
      );

      dispatch(setLoader(true));

      const getUserResp = await getUser();

      const sessionData = { ...getUserResp?.data, token: sessionUserToken };

      dispatch(setUserDetails(sessionData));

      dispatch(setLoader(false));

      if (pathname === ROUTES.login) {
        navigation('/');
      }
    } catch (error) {
      dispatch(setLoader(false));
      // in case of failure, clear session storage and redirect to login
      handleLogout();
      notification(
        'error',
        error?.response?.data?.error
          ? error?.response?.data?.error
          : error?.message
      );
    }
  };

  const handleCheckUserSession = async () => {
    const sessionUserID = JSON.parse(
      window.sessionStorage.getItem('authorization')
    )?.token;

    // User found at sessionStorage but not at redux
    // Redux is set by finding user id from session
    if (!Object.entries(userDetails)?.length && sessionUserID) {
      loadUserDetails(sessionUserID);
      return;
    }

    //User Not Logged in
    // Excluded Non guarded routes here to avoid login redirection
    const nonGuardedRoutes = [
      ROUTES.login,
      ROUTES.resetPassword,
      ROUTES.setPassword,
      ROUTES.sessionExpired,
      ROUTES.onboarding
    ];

    if (
      !Object.entries(userDetails)?.length &&
      !sessionUserID &&
      !nonGuardedRoutes.some((route) => pathname.includes(route))
    ) {
      return navigation(`${ROUTES.login}/${encodeURIComponent(pathname)}`);
    }

    // User Logged In but still tries to open Login Page or Session Expired Page
    // Redirects to /
    if (
      Object.entries(userDetails)?.length &&
      sessionUserID &&
      (pathname.includes(ROUTES.login) ||
        pathname.includes(ROUTES.sessionExpired))
    ) {
      return navigation('/');
    }
  };

  const handleLogout = async (navigateToLogin = true) => {
    wsDisconnect();
    dispatch(logout());
    window.sessionStorage.setItem('authorization', JSON.stringify({}));
    setUserAuthToken(null);
    setUserRefreshToken(null);
    if (navigateToLogin) {
      navigation(ROUTES.login);
    }
  };

  useEffect(() => {
    handleCheckUserSession();
  }, [pathname]);

  return (
    <AuthContext.Provider
      value={{ handleLogout: handleLogout, loadUserDetails: loadUserDetails }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default Auth;
